import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, Globe, Heart, Phone, Flag, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../services/api';
import { BottomNav } from '../components/BottomNav';

export default function SafetyPage() {
  const navigate = useNavigate();
  const [tips, setTips] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTips();
  }, []);

  const loadTips = async () => {
    try {
      const response = await contentAPI.safetyTips();
      setTips(response.data.safety_tips);
    } catch (error) {
      console.error('Failed to load safety tips:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { key: 'verification_tips', title: 'Profile Verification', icon: Shield, color: 'text-green-400' },
    { key: 'international_dating_tips', title: 'International Dating', icon: Globe, color: 'text-blue-400' },
    { key: 'meeting_safety', title: 'Meeting Safety', icon: Heart, color: 'text-pink-400' },
    { key: 'scam_prevention', title: 'Scam Prevention', icon: AlertTriangle, color: 'text-yellow-400' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pb-24" data-testid="safety-page">
      {/* Header */}
      <header className="sticky top-0 bg-dark/95 backdrop-blur-xl border-b border-white/10 z-40">
        <div className="flex items-center gap-4 px-6 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="font-heading text-2xl text-white">Safety Center</h1>
            <p className="text-sm text-gray-400 font-body">Stay safe while dating</p>
          </div>
        </div>
      </header>

      {/* Emergency banner */}
      <div className="mx-6 mt-6 p-4 bg-red-500/10 border border-red-500/30 rounded-2xl">
        <div className="flex items-center gap-3">
          <Phone className="w-6 h-6 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-400 font-semibold font-body">Emergency?</p>
            <p className="text-sm text-gray-300 font-body">
              If you're in immediate danger, contact local emergency services
            </p>
          </div>
        </div>
      </div>

      {/* Report button */}
      <div className="mx-6 mt-4">
        <button className="w-full p-4 bg-dark-paper border border-white/10 rounded-2xl flex items-center gap-3 hover:border-gold/30 transition-colors">
          <Flag className="w-6 h-6 text-gold" />
          <div className="text-left">
            <p className="text-white font-body font-semibold">Report a User</p>
            <p className="text-sm text-gray-400 font-body">Help us keep the community safe</p>
          </div>
        </button>
      </div>

      {/* Safety tips */}
      <div className="px-6 py-6 space-y-6">
        {tips && categories.map((category, index) => {
          const Icon = category.icon;
          const tipList = tips[category.key] || [];
          
          return (
            <motion.div
              key={category.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-dark-paper border border-white/5 rounded-2xl overflow-hidden"
            >
              <div className="p-4 border-b border-white/5 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-dark-elevated flex items-center justify-center ${category.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h2 className="font-heading text-lg text-white">{category.title}</h2>
              </div>
              <ul className="p-4 space-y-3">
                {tipList.map((tip, tipIndex) => (
                  <li key={tipIndex} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-gold text-xs">{tipIndex + 1}</span>
                    </span>
                    <p className="text-gray-300 font-body text-sm">{tip}</p>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>

      {/* Additional resources */}
      <div className="px-6 pb-6">
        <h2 className="font-heading text-xl text-white mb-4">Resources</h2>
        <div className="grid grid-cols-2 gap-3">
          <button 
            onClick={() => navigate('/country-guides')}
            className="p-4 bg-dark-paper border border-white/5 rounded-xl text-left hover:border-gold/30 transition-colors"
          >
            <Globe className="w-6 h-6 text-gold mb-2" />
            <p className="text-white font-body text-sm font-medium">Country Guides</p>
            <p className="text-gray-400 text-xs font-body">Dating culture info</p>
          </button>
          <button 
            onClick={() => navigate('/success-stories')}
            className="p-4 bg-dark-paper border border-white/5 rounded-xl text-left hover:border-gold/30 transition-colors"
          >
            <Heart className="w-6 h-6 text-gold mb-2" />
            <p className="text-white font-body text-sm font-medium">Success Stories</p>
            <p className="text-gray-400 text-xs font-body">Real love stories</p>
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
